import { useRef, useEffect, useCallback } from 'react';
import { Body } from '../physics/body';
import { Vector2 } from '../physics/vector';
import { PhysicsWorld } from '../physics/world';

interface Props {
  world: PhysicsWorld;
  bodies: Body[];
  selectedId: number | null;
  showPredictions: boolean;
  onSelect: (id: number | null) => void;
  onAddBody: (pos: Vector2, vel: Vector2, mass: number, color: string) => void;
}

export default function SimulationCanvas({
  world,
  bodies,
  selectedId,
  showPredictions,
  onSelect,
  onAddBody,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraRef = useRef({ x: 0, y: 0, zoom: 1 });
  const dragRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    worldStart: Vector2;
    launchStart: Vector2 | null;
  }>({ active: false, startX: 0, startY: 0, worldStart: new Vector2(0, 0), launchStart: null });

  const toWorld = useCallback((screenX: number, screenY: number) => {
    const cam = cameraRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return new Vector2(0, 0);
    const rect = canvas.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    return new Vector2(
      (screenX - rect.left - cx) / cam.zoom + cam.x,
      (screenY - rect.top - cy) / cam.zoom + cam.y
    );
  }, []);

  const toScreen = useCallback((worldX: number, worldY: number) => {
    const cam = cameraRef.current;
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    return {
      x: (worldX - cam.x) * cam.zoom + cx,
      y: (worldY - cam.y) * cam.zoom + cy,
    };
  }, []);

  // Render loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let raf = 0;
    const render = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const cam = cameraRef.current;

      // Background
      ctx.fillStyle = '#050508';
      ctx.fillRect(0, 0, rect.width, rect.height);

      // Grid
      ctx.strokeStyle = 'rgba(255,255,255,0.04)';
      ctx.lineWidth = 1;
      const gridSize = 50 * cam.zoom;
      const offsetX = ((cam.x * cam.zoom) % gridSize + gridSize) % gridSize;
      const offsetY = ((cam.y * cam.zoom) % gridSize + gridSize) % gridSize;
      for (let x = -offsetX; x < rect.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, rect.height);
        ctx.stroke();
      }
      for (let y = -offsetY; y < rect.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(rect.width, y);
        ctx.stroke();
      }

      // Trails
      for (const b of bodies) {
        if (b.trail.length < 2) continue;
        ctx.beginPath();
        for (let i = 0; i < b.trail.length; i++) {
          const p = toScreen(b.trail[i].x, b.trail[i].y);
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        }
        ctx.strokeStyle = b.color + '44';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Predictions
      if (showPredictions) {
        for (const b of bodies) {
          if (b.fixed) continue;
          const pred = world.predict(b, 180);
          if (pred.length < 2) continue;
          ctx.beginPath();
          for (let i = 0; i < pred.length; i++) {
            const p = toScreen(pred[i].x, pred[i].y);
            if (i === 0) ctx.moveTo(p.x, p.y);
            else ctx.lineTo(p.x, p.y);
          }
          ctx.strokeStyle = b.color + '18';
          ctx.setLineDash([4, 6]);
          ctx.lineWidth = 1;
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }

      // Bodies
      for (const b of bodies) {
        const p = toScreen(b.pos.x, b.pos.y);
        const r = Math.max(2, b.radius * cam.zoom);

        // Glow
        ctx.shadowColor = b.color;
        ctx.shadowBlur = r * 3;
        ctx.fillStyle = b.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        // Core highlight
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.beginPath();
        ctx.arc(p.x - r * 0.2, p.y - r * 0.2, r * 0.4, 0, Math.PI * 2);
        ctx.fill();

        // Selection ring
        if (b.id === selectedId) {
          ctx.strokeStyle = '#fff';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, r + 6, 0, Math.PI * 2);
          ctx.stroke();
        }
      }

      // Launch preview
      if (dragRef.current.launchStart) {
        const start = toScreen(dragRef.current.launchStart.x, dragRef.current.launchStart.y);
        const mouse = { x: dragRef.current.startX, y: dragRef.current.startY };
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Arrow head
        const angle = Math.atan2(mouse.y - start.y, mouse.x - start.x);
        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.moveTo(mouse.x, mouse.y);
        ctx.lineTo(mouse.x - 10 * Math.cos(angle - 0.5), mouse.y - 10 * Math.sin(angle - 0.5));
        ctx.lineTo(mouse.x - 10 * Math.cos(angle + 0.5), mouse.y - 10 * Math.sin(angle + 0.5));
        ctx.fill();
      }

      raf = requestAnimationFrame(render);
    };

    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [bodies, showPredictions, selectedId, world, toScreen]);

  // Mouse handlers
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const worldPos = toWorld(e.clientX, e.clientY);

    // Check if clicking on a body
    let clickedId: number | null = null;
    for (const b of bodies) {
      const sp = toScreen(b.pos.x, b.pos.y);
      const dist = Math.sqrt((x - sp.x) ** 2 + (y - sp.y) ** 2);
      if (dist < Math.max(8, b.radius * cameraRef.current.zoom + 4)) {
        clickedId = b.id;
        break;
      }
    }

    if (clickedId !== null) {
      onSelect(clickedId === selectedId ? null : clickedId);
      return;
    }

    if (e.button === 1 || e.shiftKey) {
      // Pan
      dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, worldStart: new Vector2(cameraRef.current.x, cameraRef.current.y), launchStart: null };
      canvas.setPointerCapture(e.pointerId);
    } else if (e.button === 0) {
      // Launch
      dragRef.current = { active: true, startX: x, startY: y, worldStart: new Vector2(0, 0), launchStart: worldPos };
      canvas.setPointerCapture(e.pointerId);
    }
  }, [bodies, selectedId, toWorld, toScreen, onSelect]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();

    if (dragRef.current.launchStart) {
      dragRef.current.startX = e.clientX - rect.left;
      dragRef.current.startY = e.clientY - rect.top;
    } else {
      const dx = (e.clientX - dragRef.current.startX) / cameraRef.current.zoom;
      const dy = (e.clientY - dragRef.current.startY) / cameraRef.current.zoom;
      cameraRef.current.x = dragRef.current.worldStart.x - dx;
      cameraRef.current.y = dragRef.current.worldStart.y - dy;
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current.active) return;
    const canvas = canvasRef.current;
    if (canvas) canvas.releasePointerCapture(e.pointerId);

    if (dragRef.current.launchStart) {
      const rect = canvas!.getBoundingClientRect();
      const endX = e.clientX - rect.left;
      const endY = e.clientY - rect.top;
      const dx = (dragRef.current.startX - endX) / cameraRef.current.zoom;
      const dy = (dragRef.current.startY - endY) / cameraRef.current.zoom;
      const vel = new Vector2(dx * 0.15, dy * 0.15);
      const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA'];
      const color = colors[Math.floor(Math.random() * colors.length)];
      onAddBody(dragRef.current.launchStart, vel, 15 + Math.random() * 25, color);
    }

    dragRef.current = { active: false, startX: 0, startY: 0, worldStart: new Vector2(0, 0), launchStart: null };
  }, [onAddBody]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    cameraRef.current.zoom = Math.max(0.1, Math.min(10, cameraRef.current.zoom * factor));
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', cursor: dragRef.current.launchStart ? 'crosshair' : 'default' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onWheel={handleWheel}
    />
  );
}
